import { Flex, Box, Text, Tooltip } from '@chakra-ui/react';
import {
  OutlineCard,
  ButtonText,
  Select,
  Button,
  ButtonIcon,
  useToast
} from '@morressier/alembic';
import { IUser } from '@publishing-engine/domain/models/user';
import { useState } from 'react';
import RinggoldBadge from 'src/components/RinggoldBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { OrcidIDIcon } from '../../../../../../components/User/RenderSectionDetails';

const Divider = (
  <Box
    my={2}
    borderColor="neutralPrimaryBorder"
    borderTopWidth="1px"
    borderStyle="solid"
  />
);

const AuthorCard = ({
  title,
  subtitle,
  user,
  showOrcid = false,
  isEditing = false,
  disabled = false,
  options = [],
  defaultValue,
  onEdit,
  onChange,
  shouldShowOutline = true,
  showTitleAndEditButton = true,
  isUpdateRequired
}: {
  title: string;
  subtitle: string;
  user: Partial<IUser>;
  showOrcid?: boolean;
  disabled?: boolean;
  options?: { value: string; label: string }[];
  defaultValue?: string;
  isEditing?: boolean;
  onChange: (selectedAuthor: string) => void;
  onEdit: (isEditing: boolean) => void;
  shouldShowOutline?: boolean;
  showTitleAndEditButton?: boolean;
  isUpdateRequired?: boolean;
}) => {
  const [selectedAuthor, setSelectedAuthor] = useState(defaultValue || '');
  const toast = useToast();
  const handleSaveChanges = () => {
    if (onChange) {
      onChange(selectedAuthor);
      if (!isUpdateRequired)
        toast({
          variant: 'success',
          title: `${title} Updated`,
          isClosable: true
        });
    }
  };

  const renderAuthorInfo = () => {
    return (
      <>
        <Flex
          flexDirection={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
          gap={{ base: 2, md: 4 }}
        >
          <Flex flexDirection="column" gap={1} minW={0}>
            <Text textStyle="headingItem">
              {[user.firstName, user.middleName, user.lastName]
                .filter(Boolean)
                .join(' ')}
            </Text>

            {user.email && (
              <Text
                textStyle="textBody"
                noOfLines={1}
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {user.email}
              </Text>
            )}
          </Flex>
          <Flex gap={1} alignItems="center" minW={0}>
            {user.affiliationDetails?.ringgoldId && <RinggoldBadge />}
            {user.affiliationDetails?.organizationName ? (
              <Text
                textStyle="textBody"
                noOfLines={1}
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {user.affiliationDetails?.organizationName}
                {user.affiliationDetails?.country &&
                  `, ${user.affiliationDetails.country}`}
              </Text>
            ) : (
              <Text color="actionSecondaryCautionContent" textStyle="textBody">
                No Affiliation Data
              </Text>
            )}
          </Flex>
          <Box ml={showTitleAndEditButton ? '0' : '10'}>
            <ButtonText
              hidden={!showTitleAndEditButton}
              disabled={disabled}
              onClick={() => onEdit(true)}
              alignSelf={{ base: 'flex-start', md: 'auto' }}
            >
              Edit
            </ButtonText>
          </Box>
        </Flex>
        {showOrcid && user.orcid && (
          <Flex alignItems="center" mt={1} flexDirection="row" gap={1}>
            <OrcidIDIcon value={user.orcid} />
            <Text textStyle="textContext">{user.orcid}</Text>
          </Flex>
        )}
      </>
    );
  };

  const renderIsEditing = () => (
    <>
      <Text textStyle="headingItem">Author Information</Text>
      <Text>Select an author from this submission's author list</Text>
      <Select
        value={selectedAuthor}
        onChange={e => setSelectedAuthor(e.target.value)}
      >
        <option value="" disabled hidden>
          Select author
        </option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
      <Flex
        justifyContent={{ base: 'stretch', md: 'flex-end' }}
        mt={4}
        gap={2}
        direction={{ base: 'column', sm: 'row' }}
      >
        <Button
          variant="secondary"
          onClick={() => {
            setSelectedAuthor(defaultValue || '');
            onEdit(false);
          }}
          width={{ base: '100%', sm: 'auto' }}
        >
          Close
        </Button>
        <Button
          onClick={handleSaveChanges}
          colorScheme="blue"
          width={{ base: '100%', sm: 'auto' }}
        >
          Save Changes
        </Button>
      </Flex>
    </>
  );

  return (
    <OutlineCard
      flexDirection="column"
      width="100%"
      borderColor={shouldShowOutline ? 'gray.200' : 'transparent'}
    >
      <Box
        padding={{
          base: shouldShowOutline ? 3 : 0,
          md: shouldShowOutline ? 4 : 0
        }}
      >
        <Flex wrap="wrap" alignItems="center" gap={2}>
          {showTitleAndEditButton && (
            <>
              <Text textStyle="headingItem">{title}</Text>
              <Tooltip
                hasArrow
                shouldWrapChildren
                placement="top"
                label={subtitle}
                borderRadius={4}
                data-cy="info-tooltip"
              >
                <ButtonIcon
                  size="small"
                  ariaLabel="Back to workspace settings"
                  icon={<FontAwesomeIcon icon={faInfoCircle} />}
                  data-cy="info-icon"
                  ml={1}
                />
              </Tooltip>
            </>
          )}
        </Flex>

        {Divider}
        {isEditing ? renderIsEditing() : renderAuthorInfo()}
      </Box>
    </OutlineCard>
  );
};

export default AuthorCard;
